import { z } from 'zod';
import { VerdictSchema } from './verdicts.js';
/** A customer — one factory/company whose products we inspect. */
export const CustomerSchema = z.object({
    id: z.number().int().positive(),
    name: z.string(),
    notes: z.string().nullable(),
    active: z.boolean(),
    createdAt: z.string(),
    /** Pill tint. One number, never three hex values — see customerColors.ts. */
    colorHue: z.number().int().nullable().default(null),
    productCount: z.number().int().nonnegative().default(0),
    datasetCount: z.number().int().nonnegative().default(0),
});
/** A named batch of uploaded images belonging to a product. */
/** Which products appear inside a dataset, and how many images of each. */
export const SetProductSummarySchema = z.object({
    productId: z.number().int().positive(),
    productName: z.string(),
    count: z.number().int().nonnegative(),
});
export const ImageSetSchema = z.object({
    id: z.number().int().positive(),
    /** Optional DEFAULT product for new uploads — a dataset may hold several. */
    productId: z.number().int().positive().nullable(),
    customerId: z.number().int().positive().nullable(),
    name: z.string(),
    /** Products actually present in this dataset. */
    products: z.array(SetProductSummarySchema).default([]),
    notes: z.string().nullable(),
    createdAt: z.string(),
    /** Aggregates for the gallery listing. */
    itemCount: z.number().int().nonnegative().default(0),
    annotatedCount: z.number().int().nonnegative().default(0),
});
export const CreateImageSetSchema = z.object({
    /**
     * Who this dataset belongs to. Required.
     *
     * A dataset is not a dataset *of* one product — each item carries its own and
     * one set can hold several, which is the point of uploading a shoot and
     * sorting it afterwards. So the customer cannot be derived from the product;
     * it is declared here, and no item may then reference a product belonging to
     * anybody else.
     *
     * This used to be absent, and `customer` below — free text, only ever used to
     * compose the display name — was written in its place. Every set created
     * through the UI therefore had customer_id NULL while every count of "how many
     * datasets does this customer have" read customer_id. The datasets existed and
     * were invisible to their own customer.
     */
    customerId: z.coerce.number().int().positive(),
    /** Optional: the upload form's starting point, NOT ownership. */
    productId: z.coerce.number().int().positive().nullable().optional(),
    name: z.string().min(1).max(160),
    notes: z.string().max(2000).optional(),
});
/**
 * Compose the conventional set name: Customer · Product · Date.
 * Shared so the UI preview and any server-side default stay identical.
 */
export function composeSetName(customer, label, date = new Date()) {
    const iso = date.toISOString().slice(0, 10);
    return [customer?.trim() || null, label?.trim() || null, iso].filter(Boolean).join(' · ');
}
/** One image inside a set, with its inspection result and annotation status. */
export const ImageSetItemSchema = z.object({
    id: z.string(),
    imageKey: z.string(),
    /** The product this image was inspected as (images in a set may differ). */
    productId: z.number().int().positive().nullable(),
    productName: z.string().nullable(),
    inspectionId: z.string().uuid().nullable(),
    filename: z.string().nullable(),
    verdict: VerdictSchema.nullable(),
    score: z.number().nullable(),
    annotated: z.boolean(),
    annotatedLabel: VerdictSchema.nullable(),
    createdAt: z.string(),
});
//# sourceMappingURL=imageSets.js.map